import React, { useMemo, useState, useEffect } from 'react'
import { useLang } from '../lib/useLang'
import ModTooltip from '../ui/ModTooltip/ModTooltip'

function MetricTooltipContent({ definition }) {
  const { t } = useLang()
  if (!definition) return null
  return (
    <div className='max-w-xs space-y-2 p-1'>
      <div className='font-medium text-ink'>{definition.name}</div>
      <div className='text-sm text-ink/60'>{definition.definition}</div>
      {definition.business && (
        <div className='text-sm text-ink/55'>
          <span className='font-medium text-accent'>{t('artifacts.context')}</span> {definition.business}
        </div>
      )}
      {definition.formula && (
        <div className='font-mono text-xs text-ink/50'>
          <span className='text-ink/45'>{t('artifacts.formula')}</span> {definition.formula}
        </div>
      )}
    </div>
  )
}

function MetricCard({ name, value, explained }) {
  const formattedValue = typeof value === 'number'
    ? (value < 1 && value > 0 ? (value * 100).toFixed(1) + '%' : value.toFixed(2))
    : String(value)
  const displayName = explained?.display_name || name

  const content = (
    <div
      className='group flex flex-col items-center justify-between rounded-lg p-6 text-center transition-all duration-200'
      style={{
        background: 'rgba(255, 255, 255, 0.4)',
        border: '1px solid var(--hairline-strong)',
        minHeight: '180px',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--ink)'
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 14px 40px -22px rgba(14, 14, 16, 0.16)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--hairline-strong)'
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <div className='flex w-full items-center justify-center gap-2'>
        <span
          className='text-[11px] font-semibold uppercase tracking-[0.16em]'
          style={{ color: 'var(--muted)' }}
        >
          {displayName}
        </span>
        {explained && (
          <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className='text-muted'>
            <circle cx='12' cy='12' r='10' />
            <line x1='12' y1='16' x2='12' y2='12' />
            <line x1='12' y1='8' x2='12.01' y2='8' />
          </svg>
        )}
      </div>
      <div
        className='font-light tracking-tight'
        style={{ fontSize: 'clamp(2rem, 3vw, 2.75rem)', color: 'var(--ink)', lineHeight: 1 }}
      >
        {formattedValue}
      </div>
      {explained?.interpretation && (
        <div className='text-xs leading-relaxed' style={{ color: 'rgba(14, 14, 16, 0.6)' }}>
          {explained.interpretation}
        </div>
      )}
    </div>
  )

  if (explained) {
    return (
      <ModTooltip
        content={
          <MetricTooltipContent
            definition={{
              name: displayName,
              definition: explained.definition,
              business: explained.interpretation,
            }}
          />
        }
        delay={150}
      >
        {content}
      </ModTooltip>
    )
  }

  return content
}

function DownloadCard({ icon, title, description, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className='group flex h-full w-full flex-col items-center justify-center rounded-lg p-8 text-center transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-40'
      style={{
        background: 'rgba(255, 255, 255, 0.55)',
        border: '1px solid var(--hairline-strong)',
      }}
      onMouseEnter={e => {
        if (!disabled) {
          e.currentTarget.style.borderColor = 'var(--ink)'
          e.currentTarget.style.background = '#ffffff'
          e.currentTarget.style.transform = 'translateY(-4px)'
          e.currentTarget.style.boxShadow = '0 14px 40px -20px rgba(14, 14, 16, 0.18)'
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--hairline-strong)'
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.55)'
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <div
        className='mb-4 flex h-14 w-14 items-center justify-center rounded-lg transition-colors'
        style={{ background: 'rgba(255, 90, 31, 0.1)' }}
      >
        {icon}
      </div>
      <h3 className='mb-2 text-base font-medium text-ink'>{title}</h3>
      <p className='text-sm text-muted'>{description}</p>
    </button>
  )
}

export default function ArtifactsView({ meta, fallbackMetrics, onDownloadModel, workflowId }) {
  const { t } = useLang()
  const model = meta?.last_model || null
  const [reportData, setReportData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)

  useEffect(() => {
    const wid = workflowId || model?.workflow_id
    if (!wid) {
      setReportData(null)
      return
    }

    const fetchReport = async () => {
      setReportLoading(true)
      try {
        const response = await fetch(`/api/workflow/${wid}/report/status`)
        if (response.ok) {
          const data = await response.json()
          setReportData(data)
        }
      } catch (err) {
        console.error('Failed to fetch report status:', err)
      } finally {
        setReportLoading(false)
      }
    }

    fetchReport()
  }, [workflowId, model?.workflow_id])

  const metrics = useMemo(() => {
    if (model?.metrics && Object.keys(model.metrics || {}).length) return model.metrics
    return fallbackMetrics || {}
  }, [model, fallbackMetrics])

  const summaryData = reportData?.summary || meta?.summary
  const metricsEntries = Object.entries(metrics || {})
  const primaryMetrics = ['accuracy', 'f1', 'roc_auc', 'precision', 'recall']

  return (
    <div className='flex h-full min-w-0 flex-col overflow-hidden'>
      {/* Header */}
      <div className='px-10 py-8 text-center' style={{ borderBottom: '1px solid var(--hairline-strong)' }}>
        <h2 className='text-3xl font-medium tracking-tight text-ink'>{t('artifacts.results')}</h2>
        <p className='mt-2 text-sm text-muted'>{t('artifacts.subtitle')}</p>
        {(workflowId || model?.workflow_id) && (
          <span className='mt-3 inline-block font-mono text-xs' style={{ color: 'var(--muted)' }}>
            {(workflowId || model?.workflow_id).slice(0, 12)}
          </span>
        )}
      </div>

      {/* Content */}
      <div
        className='flex-1 overflow-y-auto px-10 py-10'
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(14, 14, 16, 0.2) transparent' }}
      >
        {/* ── SUMMARY SECTION ── */}
        <section className='mb-14'>
          <div className='mb-6 flex items-center gap-3'>
            <span
              className='inline-flex items-center justify-center'
              style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255, 90, 31, 0.12)' }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                <polyline points='14,2 14,8 20,8' />
                <line x1='16' y1='13' x2='8' y2='13' />
                <line x1='16' y1='17' x2='8' y2='17' />
              </svg>
            </span>
            <div>
              <div className='text-xs font-semibold uppercase tracking-[0.18em] text-accent'>
                {t('artifacts.summaryLabel') || 'Summary'}
              </div>
              <h3 className='text-xl font-medium tracking-tight text-ink'>
                {t('artifacts.summary')}
              </h3>
            </div>
          </div>

          <div
            className='rounded-lg p-8'
            style={{
              background: 'rgba(255, 255, 255, 0.4)',
              border: '1px solid var(--hairline-strong)',
            }}
          >
            {summaryData ? (
              <>
                <p className='text-base leading-relaxed' style={{ color: 'rgba(14, 14, 16, 0.78)' }}>
                  {t('artifacts.modelTrained')}
                </p>
                <p className='mt-4 text-base leading-relaxed' style={{ color: 'rgba(14, 14, 16, 0.78)' }}>
                  {t('artifacts.reviewMetrics')}
                </p>
              </>
            ) : (
              <p className='text-base leading-relaxed' style={{ color: 'var(--muted)' }}>
                {t('artifacts.notFinished')}
              </p>
            )}
          </div>

          <div className='mt-6 grid gap-4 sm:grid-cols-3'>
            <DownloadCard
              icon={
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                  <path d='M12 2L2 7l10 5 10-5-10-5z' />
                  <path d='M2 17l10 5 10-5' />
                  <path d='M2 12l10 5 10-5' />
                </svg>
              }
              title={t('artifacts.trainedModel')}
              description={t('artifacts.pklPackage')}
              onClick={() => {
                const wid = workflowId || model?.workflow_id
                if (wid) window.open(`/api/workflow/${wid}/model`, '_blank')
              }}
              disabled={!workflowId && !model?.workflow_id}
            />
            <DownloadCard
              icon={
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                  <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                  <polyline points='14,2 14,8 20,8' />
                  <line x1='16' y1='13' x2='8' y2='13' />
                  <line x1='16' y1='17' x2='8' y2='17' />
                </svg>
              }
              title={t('artifacts.presentation')}
              description={t('artifacts.pptxReport')}
              onClick={() => {
                if (workflowId) window.open(`/api/workflow/${workflowId}/report/download?format=pptx`, '_blank')
              }}
              disabled={!workflowId}
            />
            <DownloadCard
              icon={
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                  <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                  <polyline points='14,2 14,8 20,8' />
                </svg>
              }
              title={t('artifacts.documentation')}
              description={t('artifacts.docxAnalysis')}
              onClick={() => {
                if (workflowId) window.open(`/api/workflow/${workflowId}/report/download?format=docx`, '_blank')
              }}
              disabled={!workflowId}
            />
          </div>
        </section>

        {/* Divider */}
        <div className='mb-14 h-px' style={{ background: 'var(--hairline-strong)' }} />

        {/* ── METRICS SECTION ── */}
        <section>
          <div className='mb-6 flex items-center gap-3'>
            <span
              className='inline-flex items-center justify-center'
              style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255, 90, 31, 0.12)' }}
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='text-accent'>
                <path d='M3 3v18h18' />
                <path d='m19 9-5 5-4-4-3 3' />
              </svg>
            </span>
            <div>
              <div className='text-xs font-semibold uppercase tracking-[0.18em] text-accent'>
                {t('artifacts.metricsTab') || 'Metrics'}
              </div>
              <h3 className='text-xl font-medium tracking-tight text-ink'>
                {t('artifacts.metricsTab')}
              </h3>
            </div>
          </div>

          {metricsEntries.length === 0 ? (
            <div
              className='flex h-44 flex-col items-center justify-center rounded-lg text-center'
              style={{ background: 'rgba(255, 255, 255, 0.5)', border: '1px solid var(--hairline-strong)' }}
            >
              <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className='mb-3' style={{ color: 'var(--muted)' }}>
                <path d='M3 3v18h18' />
                <path d='m19 9-5 5-4-4-3 3' />
              </svg>
              <p className='text-sm' style={{ color: 'var(--muted)' }}>{t('artifacts.noMetrics')}</p>
            </div>
          ) : (
            <div className='space-y-10'>
              {/* Primary Metrics */}
              <div>
                <h4 className='mb-5 text-[11px] font-semibold uppercase tracking-[0.18em]' style={{ color: 'var(--muted)' }}>
                  {t('artifacts.primaryMetrics')}
                </h4>
                <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                  {metricsEntries
                    .filter(([k]) => primaryMetrics.some(pm => k.toLowerCase().includes(pm)))
                    .slice(0, 6)
                    .map(([k, v]) => (
                      <MetricCard
                        key={k}
                        name={k}
                        value={v}
                        explained={summaryData?.metrics_explained?.[k]}
                      />
                    ))}
                </div>
              </div>

              {/* Additional Metrics */}
              {metricsEntries.filter(([k]) => !primaryMetrics.some(pm => k.toLowerCase().includes(pm))).length > 0 && (
                <div>
                  <h4 className='mb-5 text-[11px] font-semibold uppercase tracking-[0.18em]' style={{ color: 'var(--muted)' }}>
                    {t('artifacts.additionalMetrics')}
                  </h4>
                  <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {metricsEntries
                      .filter(([k]) => !primaryMetrics.some(pm => k.toLowerCase().includes(pm)))
                      .map(([k, v]) => (
                        <MetricCard
                          key={k}
                          name={k}
                          value={v}
                          explained={summaryData?.metrics_explained?.[k]}
                        />
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
